import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import DatePicker from 'react-datepicker';
import 'react-datepicker/dist/react-datepicker.css';
import { TRAVELER_PROFILES, TRAVEL_PACE_OPTIONS, BUDGET_OPTIONS, WHO_IS_COMING_OPTIONS } from '../constants/travelerProfiles';
import { useTokens } from '../contexts/TokenContext';
import TokenGateModal from '../components/ui/TokenGateModal';
import { TOKEN_COSTS } from '../constants/tokens';
import { MapPin, Calendar, Users, DollarSign, Gauge, Sparkles, ArrowRight, ArrowLeft, Check, Search, X, Globe, BookOpen, Plane, Train, Car, Shuffle, Map as MapIcon, Castle, Flag, Landmark, Compass, Waves, Lightbulb, PencilLine } from 'lucide-react';
import { Link } from 'react-router-dom';
import { supabase } from '../supabaseClient';
import { IconFromName } from '../utils/iconResolver';

const MAPBOX_TOKEN = process.env.REACT_APP_MAPBOX_TOKEN;

// Serialize a Date object to local YYYY-MM-DD (avoids UTC timezone shift)
const toLocalDateStr = (date) => {
  if (!date) return null;
  return `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}-${String(date.getDate()).padStart(2, '0')}`;
};

// Regions for "Undecided" flow
const REGIONS = [
  { id: 'europe', name: 'Europe', icon: Castle, description: 'France, Italy, Spain, Greece...' },
  { id: 'north-america', name: 'North America', icon: Flag, description: 'USA, Canada, Mexico...' },
  { id: 'south-america', name: 'South America', icon: Globe, description: 'Brazil, Argentina, Peru...' },
  { id: 'africa', name: 'Africa', icon: Globe, description: 'Morocco, South Africa, Kenya...' },
  { id: 'south-east-asia', name: 'South & East Asia', icon: Landmark, description: 'Japan, Thailand, Bali...' },
  { id: 'north-central-asia', name: 'North & Central Asia', icon: Compass, description: 'Turkey, UAE, India...' },
  { id: 'oceania', name: 'Oceania', icon: Waves, description: 'Australia, New Zealand, Fiji...' },
];

const TRAVEL_MODE_OPTIONS = [
  { value: 'flight', label: 'Flight', icon: Plane, description: 'Flying to your destination' },
  { value: 'train', label: 'Train', icon: Train, description: 'Rail travel' },
  { value: 'car', label: 'Car / Road Trip', icon: Car, description: 'Driving adventure' },
  { value: 'van', label: 'Van / Camper', icon: Car, description: 'Van life road trip' },
  { value: 'mixed', label: 'Mixed', icon: Shuffle, description: 'Combination of transport' },
];

const STEPS = [
  { id: 1, title: 'Destination' },
  { id: 2, title: 'Travel From' },
  { id: 3, title: 'Dates' },
  { id: 4, title: 'Pace' },
  { id: 5, title: 'Profile' },
  { id: 6, title: "Who's Coming" },
  { id: 7, title: 'Budget' },
  { id: 8, title: 'Your Trip' },
  { id: 9, title: 'Generate' },
];

const TRAVEL_TIPS = [
  'Roll your clothes instead of folding to save 30% luggage space.',
  'Download offline maps before your trip — they work without data.',
  'Pack a portable charger. Your phone is your camera, map, and translator.',
  'Learn "hello", "thank you", and "sorry" in the local language.',
  'Take a photo of your hotel address in the local script for taxi drivers.',
  'Eat where the locals eat — the best food is rarely in tourist areas.',
  'Bring a reusable water bottle — many airports have refill stations past security.',
  'Always carry a small amount of local cash for markets and tips.',
  'Book experiences for the first morning — jet lag wakes you up early anyway.',
  'Wear your heaviest shoes on the plane to save luggage weight.',
  'Travel insurance costs less than a single cancelled flight — always get it.',
  'The best photos are taken during golden hour: sunrise and sunset.',
  'A sarong doubles as a towel, blanket, picnic mat, and privacy curtain.',
  'Notify your bank before traveling to avoid your cards being blocked.',
  'Split cash and cards between bags — never keep everything in one place.',
  'Airport lounges often cost less than a restaurant meal. Check day passes.',
  'Museums are often free on the first Sunday of the month in Europe.',
  'Packing cubes are the single best travel gear investment you can make.',
  'Screenshot your boarding pass — phone batteries die at the worst times.',
  'Book flights on Tuesdays and Wednesdays for the cheapest fares.',
  'Bring a doorstop for extra hotel room security — lightweight and effective.',
  'The window seat has a wall to lean on. The aisle seat has legroom. Choose wisely.',
];

function Toggle({ checked, onChange, activeClass = 'bg-coral-400' }) {
  return (
    <button
      type="button"
      role="switch"
      aria-checked={checked}
      onClick={() => onChange(!checked)}
      className={`relative inline-flex h-7 w-12 items-center rounded-full transition-colors ${
        checked ? activeClass : 'bg-platinum-300'
      }`}
    >
      <span className={`inline-block h-5 w-5 rounded-full bg-white shadow-md transition-transform ${
        checked ? 'translate-x-6' : 'translate-x-1'
      }`} />
    </button>
  );
}

const REGION_COLORS = ['bg-coral-50', 'bg-columbia-50', 'bg-naples-50', 'bg-coral-50', 'bg-columbia-50', 'bg-naples-50', 'bg-coral-50'];
const BUDGET_COLORS = { low: 'bg-green-50', medium: 'bg-columbia-50', high: 'bg-naples-50', luxury: 'bg-coral-50' };
const MODE_COLORS = { flight: 'bg-columbia-100', train: 'bg-naples-100', car: 'bg-coral-100', mixed: 'bg-platinum-100' };

function CreateItineraryPage() {
  const navigate = useNavigate();
  const location = useLocation();
  const [currentStep, setCurrentStep] = useState(0);
  const [tripMode, setTripMode] = useState(null); // 'ai' | 'blank'
  const [loading, setLoading] = useState(false);
  const [loadingMessage, setLoadingMessage] = useState('Creating...');
  const [tokenGateOpen, setTokenGateOpen] = useState(false);
  const { canAfford, deductTokens } = useTokens();
  const [loadingProgress, setLoadingProgress] = useState(0);
  const [currentTipIndex, setCurrentTipIndex] = useState(0);
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [filteredDestinations, setFilteredDestinations] = useState([]);
  const [isMultiDestination, setIsMultiDestination] = useState(false);
  const [destinations, setDestinations] = useState([]); // For multi-destination mode
  const [destinationInput, setDestinationInput] = useState(''); // Current input for multi-dest
  const [showUndecidedModal, setShowUndecidedModal] = useState(false);
  const destinationRef = useRef(null);
  // Origin autocomplete state
  const [showOriginSuggestions, setShowOriginSuggestions] = useState(false);
  const [filteredOrigins, setFilteredOrigins] = useState([]);
  const originRef = useRef(null);
  const [destDays, setDestDays] = useState({}); // { "London": 4, "Paris": 3 } — per-destination day counts
  const [destDaysUndecided, setDestDaysUndecided] = useState({}); // { "London": false, "Paris": true }
  const [destinationType, setDestinationType] = useState(null); // 'city' | 'country' | 'region' | null
  const [countryPreferredCities, setCountryPreferredCities] = useState([]);
  const [countryCityInput, setCountryCityInput] = useState('');
  const [showCountryCitySuggestions, setShowCountryCitySuggestions] = useState(false);
  const [filteredCountryCities, setFilteredCountryCities] = useState([]);
  const countryCityRef = useRef(null);

  // Check for prefilled destination from navigation state
  const prefillDestination = location.state?.prefillDestination || '';

  const [formData, setFormData] = useState({
    destination: prefillDestination,
    region: '', // For Undecided flow
    tripOrigin: '',
    travelMode: '',
    tripLength: 7,
    startDate: null,
    endDate: null,
    flexibleDates: false,
    travelPace: 'balanced',
    budget: 'medium',
    travelerProfiles: [],
    whoIsComing: [],
    mustDoActivities: '',
    tripVibe: '',
    avoidList: '',
  });

  // The wizard is one long scrolling page with no inner scroll container, so
  // the window keeps whatever scroll offset it had on the previous step. If
  // that step was tall and the user scrolled down to hit Continue, landing on
  // a shorter next step puts the viewport past its content, at the
  // Back/Continue bar and footer.
  //
  // An animated (`behavior: 'smooth'`) reset isn't enough: if the user was
  // still mid-trackpad-momentum when they clicked, that momentum keeps
  // fighting the ~300-500ms scroll animation and can win, landing the
  // viewport mid-step instead of at the top. Reset instantly instead, fire
  // it from the click itself (earliest possible, before React even
  // re-renders) as well as the step-change effect, and reapply once more
  // after the next paint to catch any momentum that was still queued.
  const resetScroll = () => {
    window.scrollTo(0, 0);
    requestAnimationFrame(() => window.scrollTo(0, 0));
  };

  useEffect(() => {
    resetScroll();
  }, [currentStep]);

  // Cycle loading messages while waiting for generation
  useEffect(() => {
    if (!loading) { setLoadingMessage('Creating...'); return; }
    const messages = [
      'Waking up the server...',
      'Planning your days...',
      'Finding the best spots...',
      'Adding activities...',
      'Crafting your itinerary...',
      'Almost there...',
    ];
    let i = 0;
    setLoadingMessage(messages[0]);
    const timer = setInterval(() => {
      i = Math.min(i + 1, messages.length - 1);
      setLoadingMessage(messages[i]);
    }, 8000);
    return () => clearInterval(timer);
  }, [loading]);

  // Fake progress bar — fast to 15%, then slow crawl to 90%
  useEffect(() => {
    if (!loading) { setLoadingProgress(0); return; }
    setLoadingProgress(0);
    // Quick jump to 15%
    const jumpTimer = setTimeout(() => setLoadingProgress(15), 400);
    // Then crawl ~1.5% per second, slowing as it approaches 90
    const crawlTimer = setInterval(() => {
      setLoadingProgress(prev => {
        if (prev >= 90) return 90;
        const remaining = 90 - prev;
        const increment = Math.max(0.3, remaining * 0.025);
        return Math.min(90, prev + increment);
      });
    }, 1000);
    return () => { clearTimeout(jumpTimer); clearInterval(crawlTimer); };
  }, [loading]);

  // Cycle travel tips every 5.5s
  useEffect(() => {
    if (!loading) return;
    setCurrentTipIndex(Math.floor(Math.random() * TRAVEL_TIPS.length));
    const tipTimer = setInterval(() => {
      setCurrentTipIndex(prev => (prev + 1) % TRAVEL_TIPS.length);
    }, 5500);
    return () => clearInterval(tipTimer);
  }, [loading]);

  // Pre-warm the Render backend on mount so it's awake by the time user finishes the wizard
  useEffect(() => {
    const apiUrl = process.env.REACT_APP_API_URL || 'https://travelatlas.onrender.com';
    fetch(`${apiUrl}/health`, { mode: 'no-cors' }).catch(() => {});
  }, []);

  // Restore wizard state after login redirect.
  //
  // Was using supabase.auth.getUser(), which makes a network round-trip to
  // re-verify the token with the Auth server. If that hadn't resolved yet on
  // this fresh mount (right after navigate(returnTo) from LoginPage), the
  // check saw no user, gave up for good (empty deps, runs once), and the
  // user's filled-in wizard just sat unrestored in sessionStorage forever —
  // looked exactly like "I logged in and everything disappeared."
  // getSession() reads the already-local session with no network call, and
  // a short bounded retry absorbs any residual mount-timing gap.
  useEffect(() => {
    const pending = sessionStorage.getItem('pendingItinerary');
    if (!pending) return;

    let cancelled = false;

    const checkAndRestore = async (attempt = 0) => {
      const { data: { session } } = await supabase.auth.getSession();
      if (!session?.user) {
        if (cancelled) return;
        if (attempt < 5) {
          setTimeout(() => checkAndRestore(attempt + 1), 300);
        }
        return; // Gave up after ~1.5s — genuinely not logged in.
      }

      try {
        const saved = JSON.parse(pending);
        sessionStorage.removeItem('pendingItinerary');
        setFormData(prev => ({ ...prev, ...saved.formData }));
        setIsMultiDestination(saved.isMultiDestination || false);
        setTripMode(saved.tripMode || 'ai');
        // Jump to the last step so the user can review and submit
        setCurrentStep(STEPS.length);
      } catch {
        sessionStorage.removeItem('pendingItinerary');
      }
    };
    checkAndRestore();
    return () => { cancelled = true; };
  }, []);

  // If destination is prefilled, skip choice screen and go straight to step 2 (AI mode)
  useEffect(() => {
    if (prefillDestination) {
      setTripMode('ai');
      setCurrentStep(2);
    }
  }, [prefillDestination]);

  // Close suggestions when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (destinationRef.current && !destinationRef.current.contains(event.target)) {
        setShowSuggestions(false);
      }
      if (originRef.current && !originRef.current.contains(event.target)) {
        setShowOriginSuggestions(false);
      }
      if (countryCityRef.current && !countryCityRef.current.contains(event.target)) {
        setShowCountryCitySuggestions(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Mapbox geocoding search helper — returns hierarchical results (country/region/city)
  const searchMapbox = async (query, setter) => {
    if (!query || query.length < 2 || !MAPBOX_TOKEN) {
      setter([]);
      return;
    }
    try {
      const res = await fetch(
        `https://api.mapbox.com/geocoding/v5/mapbox.places/${encodeURIComponent(query)}.json?access_token=${MAPBOX_TOKEN}&types=place,country,region,locality&limit=8&language=en&autocomplete=true`
      );
      const data = await res.json();
      const queryLower = query.toLowerCase();
      const results = (data.features || []).map(f => {
        const placeType = f.place_type[0];
        let typeLabel = 'city';
        if (placeType === 'country') typeLabel = 'country';
        else if (placeType === 'region') typeLabel = 'region';
        // Build a short disambiguation label shown in the dropdown only
        // e.g. "London, England" or "London, Ontario" — but store only "London"
        const context = f.context || [];
        const countryCtx = context.find(c => c.id.startsWith('country'));
        const regionCtx = context.find(c => c.id.startsWith('region'));
        let displayName = f.text;
        if (placeType !== 'country') {
          if (regionCtx && regionCtx.text !== f.text) {
            displayName = `${f.text}, ${regionCtx.text}`;
          } else if (countryCtx) {
            displayName = `${f.text}, ${countryCtx.text}`;
          }
        }
        return {
          name: f.text, // clean primary name stored + sent to AI
          displayName,  // shown in dropdown for disambiguation
          shortName: f.text,
          type: typeLabel,
        };
      });
      // Sort: exact matches first, then by type hierarchy (country > region > city)
      const typeOrder = { country: 0, region: 1, city: 2 };
      results.sort((a, b) => {
        const aExact = a.shortName.toLowerCase().startsWith(queryLower) ? 0 : 1;
        const bExact = b.shortName.toLowerCase().startsWith(queryLower) ? 0 : 1;
        if (aExact !== bExact) return aExact - bExact;
        return (typeOrder[a.type] || 2) - (typeOrder[b.type] || 2);
      });
      setter(results);
    } catch {
      setter([]);
    }
  };

  // Debounced destination search via Mapbox
  useEffect(() => {
    const searchTerm = isMultiDestination ? destinationInput : formData.destination;
    if (searchTerm.length < 2) {
      setFilteredDestinations([]);
      return;
    }
    const timer = setTimeout(() => searchMapbox(searchTerm, (results) => {
      setFilteredDestinations(results.filter(r => !destinations.includes(r.name)));
    }), 300);
    return () => clearTimeout(timer);
  }, [formData.destination, destinationInput, destinations, isMultiDestination]);

  // Debounced country city search via Mapbox
  useEffect(() => {
    if (countryCityInput.length < 2) {
      setFilteredCountryCities([]);
      return;
    }
    const timer = setTimeout(() => searchMapbox(countryCityInput, (results) => {
      setFilteredCountryCities(results.filter(r => r.type === 'city' && !countryPreferredCities.includes(r.name)));
    }), 300);
    return () => clearTimeout(timer);
  }, [countryCityInput, countryPreferredCities]);

  // Debounced origin search via Mapbox
  useEffect(() => {
    if (formData.tripOrigin.length < 2) {
      setFilteredOrigins([]);
      return;
    }
    const timer = setTimeout(() => searchMapbox(formData.tripOrigin, setFilteredOrigins), 300);
    return () => clearTimeout(timer);
  }, [formData.tripOrigin]);

  const handleInputChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  // Date/days sync helpers
  const updateTripLength = (newLength) => {
    const clamped = Math.max(1, Math.min(365, newLength));
    setFormData(prev => {
      const updates = { tripLength: clamped };
      if (prev.startDate) {
        const end = new Date(prev.startDate);
        end.setDate(end.getDate() + clamped - 1);
        updates.endDate = end;
      }
      return { ...prev, ...updates };
    });
  };

  const updateStartDate = (date) => {
    setFormData(prev => {
      const end = new Date(date);
      end.setDate(end.getDate() + prev.tripLength - 1);
      return { ...prev, startDate: date, endDate: end };
    });
  };

  const updateEndDate = (date) => {
    setFormData(prev => {
      if (prev.startDate) {
        const diffMs = date - prev.startDate;
        const diffDays = Math.round(diffMs / (1000 * 60 * 60 * 24)) + 1;
        const clamped = Math.max(1, Math.min(365, diffDays));
        return { ...prev, endDate: date, tripLength: clamped };
      }
      return { ...prev, endDate: date };
    });
  };

  const handleDestinationSelect = (destination) => {
    if (destination.name === 'Undecided') {
      setShowUndecidedModal(true);
      setShowSuggestions(false);
      return;
    }
    if (isMultiDestination) {
      if (!destinations.includes(destination.name)) {
        setDestinations(prev => [...prev, destination.name]);
      }
      setDestinationInput('');
      setShowSuggestions(false);
    } else {
      setFormData(prev => ({ ...prev, destination: destination.name }));
      setDestinationType(destination.type || null);
      setCountryPreferredCities([]);
      setCountryCityInput('');
      setShowSuggestions(false);
    }
  };

  const handleRemoveDestination = (destName) => {
    setDestinations(prev => prev.filter(d => d !== destName));
  };

  const handleAddCustomDestination = () => {
    const trimmed = destinationInput.trim();
    if (trimmed && !destinations.includes(trimmed)) {
      setDestinations(prev => [...prev, trimmed]);
      setDestinationInput('');
    }
  };

  const handleSelectUndecided = (region) => {
    setFormData(prev => ({ ...prev, destination: 'Undecided', region }));
    setShowUndecidedModal(false);
    // Keep multi-destination state — AI will propose a multi-dest route if enabled
  };

  const handleProfileToggle = (profileId) => {
    setFormData(prev => {
      const profiles = prev.travelerProfiles.includes(profileId)
        ? prev.travelerProfiles.filter(id => id !== profileId)
        : [...prev.travelerProfiles, profileId];
      return { ...prev, travelerProfiles: profiles };
    });
  };

  // 'solo' and 'undecided' are exclusive — picking either clears any other
  // selection, and picking anything else clears those two.
  const handleWhoIsComingToggle = (value) => {
    setFormData(prev => {
      const isExclusive = value === 'solo' || value === 'undecided';
      if (prev.whoIsComing.includes(value)) {
        return { ...prev, whoIsComing: prev.whoIsComing.filter(v => v !== value) };
      }
      if (isExclusive) {
        return { ...prev, whoIsComing: [value] };
      }
      return { ...prev, whoIsComing: [...prev.whoIsComing.filter(v => v !== 'solo' && v !== 'undecided'), value] };
    });
  };

  const canProceed = () => {
    switch (currentStep) {
      case 1:
        if (formData.destination === 'Undecided' && formData.region) return true;
        if (isMultiDestination) return destinations.length > 0;
        return formData.destination.trim().length > 0;
      case 2:
        return true; // Travel From is optional
      case 3:
        return formData.tripLength > 0;
      case 4:
        return formData.travelPace !== '';
      case 5:
        return formData.travelerProfiles.length > 0 && formData.travelerProfiles.length <= 4;
      case 6:
        return true; // Who's coming is optional
      case 7:
        return formData.budget !== '';
      default:
        return true;
    }
  };

  const handleNext = () => {
    resetScroll();
    if (currentStep === 0) {
      setCurrentStep(1);
      return;
    }
    // Blank mode: after destination step, submit directly
    if (tripMode === 'blank' && currentStep === 1) {
      handleSubmit('blank');
      return;
    }
    if (canProceed() && currentStep < 9) {
      if (currentStep === 1 && isMultiDestination && destinations.length > 0 && formData.destination !== 'Undecided') {
        setFormData(prev => ({ ...prev, destination: destinations.join(', ') }));
      }
      setCurrentStep(prev => prev + 1);
    }
  };

  const handleBack = () => {
    resetScroll();
    if (currentStep > 0) {
      if (currentStep === 1) setTripMode(null);
      setCurrentStep(prev => prev - 1);
    }
  };

  const handleSubmit = async (mode = 'ai') => {
    if (!formData.destination.trim()) {
      alert('Please enter a destination');
      return;
    }

    // Traveler profiles only matter when the AI is designing the trip.
    if (mode === 'ai' && formData.travelerProfiles.length === 0) {
      alert('Please select at least one traveler profile');
      return;
    }

    if (mode === 'ai' && !canAfford(TOKEN_COSTS.GENERATE_ITINERARY)) {
      setTokenGateOpen(true);
      return;
    }

    setLoading(true);

    try {
      const { data: { user } } = await supabase.auth.getUser();

      if (!user) {
        // Save wizard state so it can be restored after login
        sessionStorage.setItem('pendingItinerary', JSON.stringify({
          formData,
          isMultiDestination,
          tripMode: mode,
        }));
        navigate('/login', { state: { returnTo: '/designer/create', message: 'Log in to create your itinerary' } });
        return;
      }

      // Try with trip_origin/travel_mode columns first, fallback without them
      let itinerary;
      const basePayload = {
        user_id: user.id,
        title: `${formData.destination} - ${formData.tripLength} days`,
        destination: formData.destination,
        trip_length: formData.tripLength,
        start_date: toLocalDateStr(formData.startDate),
        end_date: toLocalDateStr(formData.endDate),
        travel_pace: formData.travelPace,
        budget: formData.budget,
        traveler_profiles: formData.travelerProfiles,
      };

      const { data, error } = await supabase
        .from('itineraries')
        .insert({
          ...basePayload,
          trip_origin: formData.tripOrigin || null,
          travel_mode: formData.travelMode || null,
          must_do_activities: formData.mustDoActivities || null,
          trip_vibe: formData.tripVibe || null,
          avoid_list: formData.avoidList || null,
          who_is_coming: formData.whoIsComing,
        })
        .select()
        .single();

      if (error) {
        // Fallback: columns may not exist yet — retry without them
        const { data: fallbackData, error: fallbackError } = await supabase
          .from('itineraries')
          .insert(basePayload)
          .select()
          .single();
        if (fallbackError) throw fallbackError;
        itinerary = fallbackData;
      } else {
        itinerary = data;
      }

      const itineraryId = itinerary.id;

      // Blank template — skip AI generation entirely. The planner opens with
      // the trip's days laid out and empty, ready for the user to build by hand.
      if (mode === 'blank') {
        navigate(`/designer/planner/${itineraryId}`, { state: { preferences: formData, manual: true } });
        return;
      }

      const apiUrl = process.env.REACT_APP_API_URL || 'https://travelatlas.onrender.com';

      // Build destinationDays map — only include non-undecided entries
      const resolvedDestDays = {};
      if (isMultiDestination && destinations.length >= 2) {
        destinations.forEach(dest => {
          if (!destDaysUndecided[dest] && destDays[dest]) {
            resolvedDestDays[dest] = destDays[dest];
          }
        });
      }

      const genBody = JSON.stringify({
        itineraryId: itineraryId,
        destination: formData.destination,
        tripLength: formData.tripLength,
        travelPace: formData.travelPace,
        budget: formData.budget,
        travelerProfiles: formData.travelerProfiles,
        region: formData.region || null,
        tripOrigin: formData.tripOrigin || null,
        travelMode: formData.travelMode || null,
        isMultiDestination: isMultiDestination,
        mustDoActivities: formData.mustDoActivities || null,
        destinationDays: Object.keys(resolvedDestDays).length > 0 ? resolvedDestDays : null,
        destinationType: destinationType || null,
        preferredCities: countryPreferredCities.length > 0 ? countryPreferredCities : null,
        startDate: formData.startDate ? toLocalDateStr(formData.startDate) : null,
        userIntent: {
          tripVibe: formData.tripVibe || null,
          mustHave: formData.mustDoActivities || null,
          avoidList: formData.avoidList || null,
          whoIsComing: formData.whoIsComing.length > 0 ? formData.whoIsComing : null,
        },
      });
      // Wait for the backend to acknowledge the generation request before navigating.
      // This prevents the planner from polling an empty table while the server cold-starts.
      const fireCreate = async (attempt = 0) => {
        try {
          const res = await fetch(`${apiUrl}/api/ai/generate-itinerary`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: genBody,
          });
          if (res.ok) return true;
          if (attempt < 3) {
            try { await fetch(`${apiUrl}/health`, { mode: 'no-cors' }); } catch (_) {}
            await new Promise(r => setTimeout(r, attempt === 0 ? 5000 : 3000 + attempt * 3000));
            return fireCreate(attempt + 1);
          }
          return false;
        } catch (err) {
          if (attempt < 3) {
            try { await fetch(`${apiUrl}/health`, { mode: 'no-cors' }); } catch (_) {}
            await new Promise(r => setTimeout(r, attempt === 0 ? 5000 : 3000 + attempt * 3000));
            return fireCreate(attempt + 1);
          }
          console.error('Background AI generation error:', err);
          return false;
        }
      };

      const generated = await fireCreate();

      if (!generated) {
        alert('Our servers are waking up — please try again in a moment.');
        return;
      }

      deductTokens(TOKEN_COSTS.GENERATE_ITINERARY);

      // Activities are already in the DB (server processes synchronously),
      // so navigate WITHOUT generating flag — planner will fetch them immediately.
      navigate(`/designer/planner/${itineraryId}`, {
        state: { preferences: formData }
      });

    } catch (error) {
      console.error('Error creating itinerary:', error);
      alert('Failed to create itinerary. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  // Progress bar — colorful dots
  const ProgressBar = () => {
    if (currentStep === 0 || tripMode === 'blank') return null;
    return (
    <div className="mb-4 md:mb-8 flex flex-col items-center gap-3">
      <div className="flex items-center gap-2.5">
        {STEPS.map((step) => {
          const isCompleted = currentStep > step.id;
          const isCurrent = currentStep === step.id;
          return (
            <motion.div
              key={step.id}
              animate={isCurrent ? { scale: [1, 1.3, 1] } : { scale: 1 }}
              transition={isCurrent ? { repeat: Infinity, duration: 2, ease: 'easeInOut' } : {}}
              className={`rounded-full transition-all ${
                isCompleted
                  ? 'w-3 h-3 bg-coral-400'
                  : isCurrent
                  ? 'w-4 h-4 bg-naples-400'
                  : 'w-3 h-3 bg-platinum-300'
              }`}
            />
          );
        })}
      </div>
      <p className="text-sm font-semibold text-charcoal-500">
        {STEPS.find(s => s.id === currentStep)?.title}
      </p>
    </div>
    );
  };

  // Step content renderer
  const renderStepContent = () => {
    switch (currentStep) {
      case 0:
        return (
          <div className="text-center">
            <h2 className="text-3xl font-bold text-charcoal-500 mb-2">How do you want to start?</h2>
            <p className="text-platinum-600 mb-10">You can always change direction later.</p>
            <div className="grid sm:grid-cols-2 gap-4 max-w-xl mx-auto">
              <motion.button
                onClick={() => { setTripMode('ai'); setCurrentStep(1); }}
                whileHover={{ scale: 1.03 }}
                whileTap={{ scale: 0.97 }}
                className="p-7 rounded-3xl bg-coral-400 hover:bg-coral-500 text-white text-left flex flex-col gap-4 transition-colors shadow-lg"
              >
                <Sparkles size={28} />
                <div>
                  <p className="font-black text-xl mb-1.5">AI-planned trip</p>
                  <p className="text-white/80 text-sm leading-relaxed">Tell us your preferences — we'll build a full day-by-day itinerary.</p>
                </div>
              </motion.button>
              <motion.button
                onClick={() => { setTripMode('blank'); setCurrentStep(1); }}
                whileHover={{ scale: 1.03 }}
                whileTap={{ scale: 0.97 }}
                className="p-7 rounded-3xl bg-white border border-platinum-200 hover:border-platinum-300 hover:shadow-md text-left flex flex-col gap-4 transition-all shadow-sm"
              >
                <PencilLine size={28} className="text-charcoal-400" />
                <div>
                  <p className="font-black text-xl text-charcoal-500 mb-1.5">Start from scratch</p>
                  <p className="text-charcoal-400 text-sm leading-relaxed">Pick a destination and build your own itinerary, your way.</p>
                </div>
              </motion.button>
            </div>
          </div>
        );

      case 1:
        return (
          <div className="text-center">
            <h2 className="text-3xl font-bold text-charcoal-500 mb-2">
              Where do you want to go?
            </h2>
            <p className="text-platinum-600 mb-6">
              Search a city, country, or let us surprise you
            </p>

            {/* Multi-destination toggle */}
            <div className="max-w-lg mx-auto mb-5 flex items-center justify-center gap-3">
              <Toggle
                checked={isMultiDestination}
                onChange={(val) => {
                  setIsMultiDestination(val);
                  if (!val) {
                    if (destinations.length > 0) {
                      setFormData(prev => ({ ...prev, destination: destinations[0] }));
                    }
                    setDestinations([]);
                  } else {
                    if (formData.destination && formData.destination !== 'Undecided') {
                      setDestinations([formData.destination]);
                      setFormData(prev => ({ ...prev, destination: '' }));
                    }
                  }
                }}
              />
              <Globe size={18} className="text-coral-400" />
              <span className="text-charcoal-500 font-medium">Multi-destination</span>
            </div>

            {/* Multi-destination tag chips */}
            {isMultiDestination && destinations.length > 0 && (
              <div className="max-w-lg mx-auto mb-4 flex flex-wrap gap-2 justify-center">
                {destinations.map((dest) => (
                  <motion.span
                    key={dest}
                    initial={{ scale: 0.8, opacity: 0 }}
                    animate={{ scale: 1, opacity: 1 }}
                    whileHover={{ scale: 1.05 }}
                    className="inline-flex items-center gap-1.5 px-4 py-2 rounded-full text-sm font-semibold bg-coral-100 text-coral-700 shadow-sm"
                  >
                    <MapPin size={14} />
                    {dest}
                    <button
                      onClick={() => handleRemoveDestination(dest)}
                      className="hover:bg-coral-200 rounded-full p-0.5 transition-colors"
                    >
                      <X size={12} />
                    </button>
                  </motion.span>
                ))}
              </div>
            )}

            <div className="max-w-lg mx-auto relative" ref={destinationRef}>
              <div className="relative">
                <Search className="absolute left-5 top-1/2 -translate-y-1/2 text-platinum-400" size={22} />
                <input
                  type="text"
                  value={isMultiDestination ? destinationInput : formData.destination}
                  onChange={(e) => {
                    if (isMultiDestination) {
                      setDestinationInput(e.target.value);
                    } else {
                      handleInputChange('destination', e.target.value);
                      setDestinationType(null);
                      setCountryPreferredCities([]);
                    }
                  }}
                  onFocus={() => setShowSuggestions(true)}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') {
                      e.preventDefault();
                      if (isMultiDestination && destinationInput.trim()) {
                        handleAddCustomDestination();
                      } else {
                        setShowSuggestions(false);
                      }
                    }
                  }}
                  placeholder={isMultiDestination ? 'Add another destination...' : 'Search destination...'}
                  className="w-full pl-14 pr-4 py-5 text-lg bg-platinum-50 rounded-2xl shadow-lg border-0 outline-none focus:ring-2 focus:ring-coral-300 focus:bg-white transition-all placeholder:text-platinum-400"
                  autoFocus
                />
              </div>

              {showSuggestions && (
                <div className="absolute top-full left-0 right-0 mt-2 bg-white rounded-2xl shadow-xl overflow-hidden z-50 max-h-[300px] overflow-y-auto">
                  <button
                    onClick={() => handleDestinationSelect({ name: 'Undecided' })}
                    className="w-full px-5 py-3.5 text-left hover:bg-coral-50 transition-colors border-b border-platinum-100 flex items-center gap-3"
                  >
                    <Sparkles size={24} className="text-coral-400" />
                    <div>
                      <span className="font-semibold text-coral-500">Undecided</span>
                      <span className="text-sm text-platinum-500 ml-2">
                        {isMultiDestination ? '— AI picks a multi-city route' : '— Let us inspire you'}
                      </span>
                    </div>
                  </button>
                  {filteredDestinations.map((dest, index) => (
                    <button
                      key={index}
                      onClick={() => handleDestinationSelect(dest)}
                      className="w-full px-5 py-3.5 text-left hover:bg-coral-50/60 transition-colors flex items-center gap-3"
                    >
                      {dest.type === 'country' ? (
                        <Globe size={20} className="text-columbia-500" />
                      ) : dest.type === 'region' ? (
                        <MapIcon size={20} className="text-naples-500" />
                      ) : (
                        <MapPin size={20} className="text-coral-400" />
                      )}
                      <span className="font-medium">{dest.displayName || dest.name}</span>
                      <span className="text-xs bg-platinum-100 text-platinum-600 rounded-full px-2.5 py-0.5 ml-auto capitalize">
                        {dest.type}
                      </span>
                    </button>
                  ))}
                  {filteredDestinations.length === 0 && (isMultiDestination ? destinationInput : formData.destination).trim().length > 1 && (
                    <div className="px-5 py-3.5 text-sm">
                      <p className="text-green-600 flex items-center gap-1.5">
                        <Check size={14} />
                        <span className="font-medium">"{(isMultiDestination ? destinationInput : formData.destination).trim()}"</span> — accepted!
                      </p>
                      <p className="text-platinum-500 text-xs mt-1">
                        {isMultiDestination ? 'Press Enter to add it' : 'Click Continue to proceed'}
                      </p>
                    </div>
                  )}
                </div>
              )}

              {!isMultiDestination && formData.destination.trim().length > 1 && !showSuggestions &&
                formData.destination !== 'Undecided' && (
                <p className="text-sm text-green-600 mt-3 flex items-center justify-center gap-1">
                  <Check size={14} />
                  Destination accepted — we'll plan your trip!
                </p>
              )}
              {formData.destination === 'Undecided' && isMultiDestination && formData.region && !showSuggestions && (
                <p className="text-sm text-coral-600 mt-3 flex items-center justify-center gap-1">
                  <Sparkles size={14} />
                  AI will design a multi-destination route in {REGIONS.find(r => r.id === formData.region)?.name || formData.region}
                </p>
              )}
            </div>

            {/* Country city preferences — shown when user picks a country destination */}
            {!isMultiDestination && destinationType === 'country' && formData.destination && formData.destination !== 'Undecided' && (
              <div className="max-w-lg mx-auto mt-4 bg-platinum-50 rounded-2xl p-5 shadow-lg text-left">
                <h3 className="text-sm font-semibold text-charcoal-500 mb-1">Which cities do you want to visit?</h3>
                <p className="text-xs text-platinum-400 mb-3">Optional — leave empty and the AI picks the best cities for your trip length and style</p>
                {countryPreferredCities.length > 0 && (
                  <div className="flex flex-wrap gap-2 mb-3">
                    {countryPreferredCities.map(city => (
                      <span key={city} className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-semibold bg-coral-100 text-coral-700">
                        <MapPin size={11} />
                        {city}
                        <button
                          onClick={() => setCountryPreferredCities(prev => prev.filter(c => c !== city))}
                          className="hover:bg-coral-200 rounded-full p-0.5 transition-colors"
                        >
                          <X size={10} />
                        </button>
                      </span>
                    ))}
                  </div>
                )}
                <div className="relative" ref={countryCityRef}>
                  <input
                    type="text"
                    value={countryCityInput}
                    onChange={e => setCountryCityInput(e.target.value)}
                    onFocus={() => setShowCountryCitySuggestions(true)}
                    onKeyDown={e => {
                      if (e.key === 'Enter' && countryCityInput.trim()) {
                        const trimmed = countryCityInput.trim();
                        if (!countryPreferredCities.includes(trimmed)) {
                          setCountryPreferredCities(prev => [...prev, trimmed]);
                        }
                        setCountryCityInput('');
                        setShowCountryCitySuggestions(false);
                      }
                    }}
                    placeholder={`Add a city in ${formData.destination}...`}
                    className="w-full px-4 py-2.5 text-sm bg-white rounded-xl border border-platinum-200 outline-none focus:ring-2 focus:ring-coral-300 placeholder:text-platinum-400 transition-all"
                  />
                  {showCountryCitySuggestions && filteredCountryCities.length > 0 && (
                    <div className="absolute top-full left-0 right-0 mt-1.5 bg-white rounded-xl shadow-xl overflow-hidden z-50 max-h-[200px] overflow-y-auto">
                      {filteredCountryCities.map((city, i) => (
                        <button
                          key={i}
                          onClick={() => {
                            if (!countryPreferredCities.includes(city.name)) {
                              setCountryPreferredCities(prev => [...prev, city.name]);
                            }
                            setCountryCityInput('');
                            setShowCountryCitySuggestions(false);
                          }}
                          className="w-full px-4 py-2.5 text-left hover:bg-coral-50 transition-colors flex items-center gap-2 text-sm"
                        >
                          <MapPin size={14} className="text-coral-400 flex-shrink-0" />
                          <span className="font-medium">{city.displayName || city.name}</span>
                        </button>
                      ))}
                    </div>
                  )}
                </div>
                {countryPreferredCities.length === 0 && (
                  <p className="text-xs text-platinum-400 mt-2 flex items-center gap-1">
                    <Sparkles size={11} className="text-coral-300" />
                    AI will choose the best cities based on your travel style
                  </p>
                )}
              </div>
            )}

            {/* Blank mode: quick days picker — replaces the full dates step */}
            {tripMode === 'blank' && (
              <div className="max-w-lg mx-auto mt-6 py-5 px-6 bg-platinum-50 rounded-2xl shadow-sm text-center">
                <p className="text-sm font-semibold text-charcoal-500 mb-4">How many days?</p>
                <div className="flex items-center justify-center gap-5">
                  <button type="button" onClick={() => updateTripLength(formData.tripLength - 1)} className="w-11 h-11 rounded-full bg-white border border-platinum-200 hover:bg-platinum-100 text-charcoal-500 flex items-center justify-center text-xl font-bold transition-colors">-</button>
                  <span className="text-4xl font-black text-charcoal-500 w-14 text-center">{formData.tripLength}</span>
                  <button type="button" onClick={() => updateTripLength(formData.tripLength + 1)} className="w-11 h-11 rounded-full bg-white border border-platinum-200 hover:bg-platinum-100 text-charcoal-500 flex items-center justify-center text-xl font-bold transition-colors">+</button>
                </div>
              </div>
            )}

            {/* Undecided Region Selection Modal */}
            {showUndecidedModal && (
              <div className="fixed inset-0 bg-black/40 backdrop-blur-sm flex items-center justify-center z-50" onClick={() => setShowUndecidedModal(false)}>
                <motion.div
                  initial={{ scale: 0.9, opacity: 0 }}
                  animate={{ scale: 1, opacity: 1 }}
                  className="bg-white rounded-3xl shadow-2xl w-full max-w-2xl mx-4 p-8"
                  onClick={(e) => e.stopPropagation()}
                >
                  <div className="text-center mb-6">
                    <Sparkles size={40} className="text-coral-400 mb-3" />
                    <h3 className="text-2xl font-bold text-charcoal-500 mb-2">
                      Let us inspire you!
                    </h3>
                    <p className="text-platinum-600">
                      Pick a region and we'll find the perfect destination
                    </p>
                  </div>

                  <div className="grid grid-cols-2 md:grid-cols-3 gap-3 mb-6">
                    {REGIONS.map((region, i) => (
                      <motion.button
                        key={region.id}
                        onClick={() => handleSelectUndecided(region.id)}
                        whileHover={{ scale: 1.05 }}
                        whileTap={{ scale: 0.95 }}
                        className={`p-5 rounded-2xl ${REGION_COLORS[i]} hover:shadow-lg transition-shadow text-center`}
                      >
                        <region.icon size={40} className="text-charcoal-400 mb-2" />
                        <div className="font-bold text-sm text-charcoal-500">{region.name}</div>
                        <div className="text-xs text-platinum-500 mt-1">{region.description}</div>
                      </motion.button>
                    ))}
                  </div>

                  <div className="text-center pt-4">
                    <Link to="/atlas" className="inline-flex items-center gap-2 px-5 py-2.5 rounded-full bg-columbia-50 hover:bg-columbia-100 text-columbia-700 font-medium text-sm transition-colors">
                      <BookOpen size={16} />
                      Browse Atlas Files
                    </Link>
                  </div>
                </motion.div>
              </div>
            )}
          </div>
        );

      case 2:
        return (
          <div className="text-center">
            <h2 className="text-3xl font-bold text-charcoal-500 mb-2">
              Where are you traveling from?
            </h2>
            <p className="text-platinum-600 mb-2">
              Helps us plan transport and your first day
            </p>
            <p className="text-sm text-platinum-400 mb-6">Optional — you can skip this for now</p>

            <div className="max-w-lg mx-auto relative mb-8" ref={originRef}>
              <div className="relative">
                <Search className="absolute left-5 top-1/2 -translate-y-1/2 text-platinum-400" size={22} />
                <input
                  type="text"
                  value={formData.tripOrigin}
                  onChange={(e) => handleInputChange('tripOrigin', e.target.value)}
                  onFocus={() => setShowOriginSuggestions(true)}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') {
                      e.preventDefault();
                      setShowOriginSuggestions(false);
                    }
                  }}
                  placeholder="Your home city..."
                  className="w-full pl-14 pr-4 py-5 text-lg bg-platinum-50 rounded-2xl shadow-lg border-0 outline-none focus:ring-2 focus:ring-columbia-300 focus:bg-white transition-all placeholder:text-platinum-400"
                  autoFocus
                />
              </div>

              {showOriginSuggestions && (filteredOrigins.length > 0 || formData.tripOrigin.length === 0) && (
                <div className="absolute top-full left-0 right-0 mt-2 bg-white rounded-2xl shadow-xl overflow-hidden z-50 max-h-[250px] overflow-y-auto">
                  <button
                    onClick={() => {
                      handleInputChange('tripOrigin', 'Undecided');
                      setShowOriginSuggestions(false);
                    }}
                    className="w-full px-5 py-3.5 text-left hover:bg-platinum-50 transition-colors border-b border-platinum-100 flex items-center gap-3"
                  >
                    <Sparkles size={20} className="text-platinum-400" />
                    <div>
                      <span className="font-medium text-platinum-600">Decide later</span>
                      <span className="text-sm text-platinum-400 ml-2">— no transport activity added</span>
                    </div>
                  </button>
                  {filteredOrigins.map((dest, index) => (
                    <button
                      key={index}
                      onClick={() => {
                        handleInputChange('tripOrigin', dest.name);
                        setShowOriginSuggestions(false);
                      }}
                      className="w-full px-5 py-3.5 text-left hover:bg-columbia-50 transition-colors flex items-center gap-3"
                    >
                      {dest.type === 'country' ? (
                        <Globe size={20} className="text-columbia-500" />
                      ) : dest.type === 'region' ? (
                        <MapIcon size={20} className="text-naples-500" />
                      ) : (
                        <MapPin size={20} className="text-coral-400" />
                      )}
                      <span className="font-medium">{dest.name}</span>
                      <span className="text-xs bg-platinum-100 text-platinum-600 rounded-full px-2.5 py-0.5 ml-auto capitalize">
                        {dest.type}
                      </span>
                    </button>
                  ))}
                </div>
              )}

              {formData.tripOrigin.trim().length > 1 && !showOriginSuggestions && (
                <p className="text-sm text-green-600 mt-3 flex items-center justify-center gap-1">
                  <Check size={14} />
                  Origin accepted
                </p>
              )}
            </div>

            <h3 className="text-lg font-semibold text-charcoal-500 mb-2">
              How do you want to get there?
            </h3>
            <p className="text-platinum-500 mb-5 text-sm">Optional</p>

            <div className="max-w-md mx-auto grid grid-cols-2 gap-4">
              {TRAVEL_MODE_OPTIONS.map((option) => {
                const isSelected = formData.travelMode === option.value;
                return (
                  <motion.button
                    key={option.value}
                    type="button"
                    onClick={() => handleInputChange('travelMode', isSelected ? '' : option.value)}
                    whileHover={{ scale: 1.04 }}
                    whileTap={{ scale: 0.96 }}
                    className={`relative p-5 rounded-2xl transition-all text-center ${
                      isSelected
                        ? `${MODE_COLORS[option.value]} ring-2 ring-coral-300 shadow-md`
                        : 'bg-white shadow-sm hover:shadow-md'
                    }`}
                  >
                    {isSelected && (
                      <div className="absolute top-2 right-2 w-5 h-5 rounded-full bg-coral-400 text-white flex items-center justify-center">
                        <Check size={12} />
                      </div>
                    )}
                    <option.icon size={32} className="mb-2 text-charcoal-400" />
                    <div className="text-sm font-semibold">{option.label}</div>
                  </motion.button>
                );
              })}
            </div>
          </div>
        );

      case 3:
        return (
          <div className="text-center">
            <h2 className="text-3xl font-bold text-charcoal-500 mb-2">
              When are you traveling?
            </h2>
            <p className="text-platinum-600 mb-8">
              Set your trip length and dates
            </p>

            <div className="max-w-lg mx-auto space-y-6">
              {/* Day counter */}
              <div className="py-4">
                <div className="flex items-center justify-center gap-5">
                  <motion.button
                    type="button"
                    onClick={() => updateTripLength(formData.tripLength - 1)}
                    whileHover={{ scale: 1.1 }}
                    whileTap={{ scale: 0.9 }}
                    className="w-14 h-14 rounded-full bg-columbia-100 hover:bg-columbia-200 text-columbia-700 flex items-center justify-center text-2xl font-bold transition-colors shadow-sm"
                  >
                    -
                  </motion.button>
                  <div>
                    <input
                      type="number"
                      min="1"
                      max="365"
                      value={formData.tripLength}
                      onChange={(e) => {
                        const val = parseInt(e.target.value);
                        if (!isNaN(val)) updateTripLength(val);
                        else if (e.target.value === '') handleInputChange('tripLength', '');
                      }}
                      onBlur={() => {
                        if (!formData.tripLength || formData.tripLength < 1) updateTripLength(1);
                      }}
                      className="text-6xl font-black text-columbia-600 w-28 text-center bg-transparent border-none outline-none focus:ring-0 [appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none"
                    />
                    <p className="text-sm text-platinum-500 -mt-1">days</p>
                  </div>
                  <motion.button
                    type="button"
                    onClick={() => updateTripLength(formData.tripLength + 1)}
                    whileHover={{ scale: 1.1 }}
                    whileTap={{ scale: 0.9 }}
                    className="w-14 h-14 rounded-full bg-columbia-100 hover:bg-columbia-200 text-columbia-700 flex items-center justify-center text-2xl font-bold transition-colors shadow-sm"
                  >
                    +
                  </motion.button>
                </div>
              </div>

              {/* Flexible dates toggle */}
              <div className="flex items-center justify-center gap-3">
                <Toggle
                  checked={formData.flexibleDates}
                  onChange={(val) => handleInputChange('flexibleDates', val)}
                  activeClass="bg-columbia-400"
                />
                <Calendar size={18} className="text-columbia-400" />
                <span className="text-charcoal-500 font-medium">My dates are flexible</span>
              </div>

              {!formData.flexibleDates && (
                <div className="grid md:grid-cols-2 gap-4">
                  <div className="bg-platinum-50 rounded-2xl shadow-lg p-5">
                    <label className="block text-sm font-medium text-charcoal-500 mb-2">
                      Start Date
                    </label>
                    <DatePicker
                      selected={formData.startDate}
                      onChange={(date) => updateStartDate(date)}
                      className="w-full px-4 py-3 bg-white rounded-xl border-0 shadow-sm outline-none focus:ring-2 focus:ring-columbia-300"
                      placeholderText="Select date"
                      minDate={new Date()}
                    />
                  </div>
                  <div className="bg-platinum-50 rounded-2xl shadow-lg p-5">
                    <label className="block text-sm font-medium text-charcoal-500 mb-2">
                      End Date
                    </label>
                    <DatePicker
                      selected={formData.endDate}
                      onChange={(date) => updateEndDate(date)}
                      className="w-full px-4 py-3 bg-white rounded-xl border-0 shadow-sm outline-none focus:ring-2 focus:ring-columbia-300"
                      placeholderText="Select date"
                      minDate={formData.startDate || new Date()}
                    />
                  </div>
                </div>
              )}

              {/* Days per destination — only shown for multi-destination trips */}
              {isMultiDestination && destinations.length >= 2 && (
                <div className="mt-6 bg-platinum-50 rounded-2xl p-5 shadow-lg text-left">
                  <h3 className="text-base font-semibold text-charcoal-500 mb-1">Days per destination</h3>
                  <p className="text-xs text-platinum-500 mb-4">Optional — set how long you want in each place, or leave it to the AI</p>
                  <div className="space-y-3">
                    {destinations.map((dest) => {
                      const isUndecided = destDaysUndecided[dest] ?? true;
                      const defaultDays = Math.floor(formData.tripLength / destinations.length);
                      return (
                        <div key={dest} className="flex items-center gap-3">
                          <span className="flex-1 text-sm font-medium text-charcoal-500 truncate">{dest}</span>
                          {isUndecided ? (
                            <span className="text-xs text-platinum-500 px-3 py-1.5 bg-white rounded-lg border border-platinum-200 w-24 text-center">AI decides</span>
                          ) : (
                            <div className="flex items-center gap-1 bg-white rounded-lg border border-platinum-200 px-2 py-1">
                              <button
                                type="button"
                                onClick={() => setDestDays(prev => ({ ...prev, [dest]: Math.max(1, (prev[dest] || defaultDays) - 1) }))}
                                className="w-6 h-6 rounded-full bg-columbia-100 hover:bg-columbia-200 text-columbia-700 flex items-center justify-center text-sm font-bold"
                              >-</button>
                              <span className="w-8 text-center text-sm font-bold text-charcoal-500">
                                {destDays[dest] || defaultDays}
                              </span>
                              <button
                                type="button"
                                onClick={() => setDestDays(prev => ({ ...prev, [dest]: (prev[dest] || defaultDays) + 1 }))}
                                className="w-6 h-6 rounded-full bg-columbia-100 hover:bg-columbia-200 text-columbia-700 flex items-center justify-center text-sm font-bold"
                              >+</button>
                              <span className="text-xs text-platinum-500 ml-1">days</span>
                            </div>
                          )}
                          <button
                            type="button"
                            onClick={() => setDestDaysUndecided(prev => ({ ...prev, [dest]: !isUndecided }))}
                            className={`text-xs px-2.5 py-1 rounded-full font-medium transition-colors ${
                              isUndecided ? 'bg-coral-100 text-coral-600 hover:bg-coral-200' : 'bg-platinum-100 text-platinum-500 hover:bg-platinum-200'
                            }`}
                          >
                            {isUndecided ? 'Set days' : 'Let AI decide'}
                          </button>
                        </div>
                      );
                    })}
                  </div>
                  {destinations.length >= 2 && Object.values(destDaysUndecided).some(v => !v) && (
                    (() => {
                      const total = destinations.reduce((sum, d) => sum + (destDaysUndecided[d] ? 0 : (destDays[d] || Math.floor(formData.tripLength / destinations.length))), 0);
                      const diff = total - formData.tripLength;
                      if (Math.abs(diff) > 0) {
                        return (
                          <p className="text-xs text-amber-600 mt-3 flex items-center gap-1">
                            <span>⚠</span> Specified days total {total} but trip is {formData.tripLength} days. The AI will adjust the remainder.
                          </p>
                        );
                      }
                      return null;
                    })()
                  )}
                </div>
              )}

              {/* Long trip hint */}
              {formData.tripLength > 60 && (
                <p className="text-xs text-columbia-600 mt-4 text-center flex items-center justify-center gap-1">
                  <Sparkles size={12} />
                  Long trip — we'll keep 2 activities per day for full coverage and better quality.
                </p>
              )}
            </div>
          </div>
        );

      case 4: {
        const selectedPace = TRAVEL_PACE_OPTIONS.find(o => o.value === formData.travelPace);
        return (
          <div className="text-center">
            <h2 className="text-3xl font-bold text-charcoal-500 mb-2">
              What's your travel pace?
            </h2>
            <p className="text-platinum-600 mb-8">
              Balance activities with downtime
            </p>

            <div className="max-w-2xl mx-auto flex gap-3 overflow-x-auto pb-2 md:grid md:grid-cols-5 md:overflow-visible scrollbar-hide">
              {TRAVEL_PACE_OPTIONS.map((option) => {
                const isSelected = formData.travelPace === option.value;
                return (
                  <motion.button
                    key={option.value}
                    type="button"
                    onClick={() => handleInputChange('travelPace', option.value)}
                    whileHover={{ scale: 1.05 }}
                    whileTap={{ scale: 0.95 }}
                    animate={isSelected ? { scale: 1.05 } : { scale: 1 }}
                    transition={{ type: 'spring', stiffness: 300, damping: 20 }}
                    className={`flex-shrink-0 p-5 rounded-2xl transition-all text-center min-w-[100px] ${
                      isSelected
                        ? 'bg-coral-100 text-coral-700 ring-2 ring-coral-300 shadow-md'
                        : 'bg-white shadow-sm hover:shadow-md'
                    }`}
                  >
                    <IconFromName name={option.iconName} size={32} className="mb-2" style={{ color: option.color }} />
                    <div className="text-sm font-semibold">{option.label}</div>
                  </motion.button>
                );
              })}
            </div>

            <AnimatePresence mode="wait">
              {selectedPace && (
                <motion.div
                  key={selectedPace.value}
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, y: -10 }}
                  className="mt-6 max-w-lg mx-auto flex items-start gap-3 text-left"
                >
                  <div className="w-1 self-stretch rounded-full bg-coral-300 flex-shrink-0" />
                  <p className="text-sm text-charcoal-500 py-1">
                    <span className="font-bold text-coral-600">{selectedPace.label}:</span>{' '}
                    {selectedPace.description}
                  </p>
                </motion.div>
              )}
            </AnimatePresence>
          </div>
        );
      }

      case 5:
        return (
          <div className="text-center">
            <h2 className="text-3xl font-bold text-charcoal-500 mb-2">
              What kind of traveler are you?
            </h2>
            <p className="text-platinum-600 mb-2">
              Select 1-4 profiles that match your style
            </p>
            <p className={`text-sm font-medium mb-6 ${formData.travelerProfiles.length > 0 ? 'text-coral-500' : 'text-platinum-400'}`}>
              {formData.travelerProfiles.length} of 4 selected
              {formData.travelerProfiles.length > 4 && <span className="text-red-500 ml-1">(max 4)</span>}
            </p>

            <div className="max-w-3xl mx-auto grid md:grid-cols-2 gap-4">
              {TRAVELER_PROFILES.map((profile) => {
                const isSelected = formData.travelerProfiles.includes(profile.id);
                return (
                  <motion.button
                    key={profile.id}
                    type="button"
                    onClick={() => handleProfileToggle(profile.id)}
                    whileHover={{ scale: 1.02 }}
                    whileTap={{ scale: 0.98 }}
                    animate={isSelected ? { scale: 1.02 } : { scale: 1 }}
                    transition={{ type: 'spring', stiffness: 300, damping: 20 }}
                    className={`relative p-5 rounded-2xl transition-all text-left ${
                      isSelected
                        ? 'bg-coral-50 ring-2 ring-coral-300 shadow-lg'
                        : 'bg-white shadow-sm hover:shadow-lg'
                    }`}
                  >
                    {isSelected && (
                      <div className="absolute top-3 right-3 w-6 h-6 rounded-full bg-coral-400 text-white flex items-center justify-center">
                        <Check size={14} />
                      </div>
                    )}
                    <div className="flex items-start gap-4">
                      <IconFromName name={profile.iconName} size={32} className="flex-shrink-0" style={{ color: profile.color }} />
                      <div className="flex-1 min-w-0">
                        <div className="font-bold text-charcoal-500 mb-1">{profile.title}</div>
                        <div className="text-sm text-platinum-600 leading-snug">{profile.description}</div>
                      </div>
                    </div>
                  </motion.button>
                );
              })}
            </div>
          </div>
        );

      case 6:
        return (
          <div className="text-center">
            <h2 className="text-3xl font-bold text-charcoal-500 mb-2">
              Who's coming?
            </h2>
            <p className="text-platinum-600 mb-8">
              Helps us tailor activities, pacing, and accessibility
            </p>

            <div className="max-w-2xl mx-auto flex flex-wrap justify-center gap-3">
              {WHO_IS_COMING_OPTIONS.map((option) => {
                const isSelected = formData.whoIsComing.includes(option.value);
                return (
                  <motion.button
                    key={option.value}
                    type="button"
                    onClick={() => handleWhoIsComingToggle(option.value)}
                    whileHover={{ scale: 1.04 }}
                    whileTap={{ scale: 0.96 }}
                    animate={isSelected ? { scale: 1.04 } : { scale: 1 }}
                    transition={{ type: 'spring', stiffness: 300, damping: 20 }}
                    className={`flex items-center gap-2 px-5 py-3 rounded-full font-semibold text-sm transition-all ${
                      isSelected
                        ? 'bg-coral-400 text-white shadow-md'
                        : 'bg-white border border-platinum-200 text-charcoal-400 hover:border-coral-300 hover:text-coral-500 shadow-sm'
                    }`}
                  >
                    <IconFromName name={option.iconName} size={16} style={{ color: isSelected ? '#fff' : undefined }} />
                    {option.label}
                  </motion.button>
                );
              })}
            </div>
          </div>
        );

      case 7: {
        const selectedBudget = BUDGET_OPTIONS.find(o => o.value === formData.budget);
        return (
          <div className="text-center">
            <h2 className="text-3xl font-bold text-charcoal-500 mb-2">
              What's your budget?
            </h2>
            <p className="text-platinum-600 mb-8">
              Helps us pick the right accommodations and activities
            </p>

            <div className="max-w-md mx-auto grid grid-cols-2 gap-4">
              {BUDGET_OPTIONS.map((option) => {
                const isSelected = formData.budget === option.value;
                const bgColor = BUDGET_COLORS[option.value] || 'bg-platinum-50';
                return (
                  <motion.button
                    key={option.value}
                    type="button"
                    onClick={() => handleInputChange('budget', option.value)}
                    whileHover={{ scale: 1.04 }}
                    whileTap={{ scale: 0.96 }}
                    animate={isSelected ? { scale: 1.04 } : { scale: 1 }}
                    transition={{ type: 'spring', stiffness: 300, damping: 20 }}
                    className={`relative p-6 rounded-2xl transition-all text-center ${
                      isSelected
                        ? `${bgColor} ring-2 ring-coral-300 shadow-lg`
                        : `${bgColor} shadow-sm hover:shadow-md`
                    }`}
                  >
                    {isSelected && (
                      <div className="absolute top-2.5 right-2.5 w-5 h-5 rounded-full bg-coral-400 text-white flex items-center justify-center">
                        <Check size={12} />
                      </div>
                    )}
                    <div className="text-5xl mb-2">{option.symbol}</div>
                    <div className="text-sm font-bold text-charcoal-500">{option.label}</div>
                    <IconFromName name={option.iconName} size={24} className="mt-1" style={{ color: option.color }} />
                  </motion.button>
                );
              })}
            </div>

            <AnimatePresence mode="wait">
              {selectedBudget && (
                <motion.div
                  key={selectedBudget.value}
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, y: -10 }}
                  className="mt-6 max-w-lg mx-auto flex items-start gap-3 text-left"
                >
                  <div className="w-1 self-stretch rounded-full bg-coral-300 flex-shrink-0" />
                  <p className="text-sm text-charcoal-500 py-1">
                    <span className="font-bold text-coral-600">{selectedBudget.label}:</span>{' '}
                    {selectedBudget.description}
                  </p>
                </motion.div>
              )}
            </AnimatePresence>
          </div>
        );
      }

      case 8:
        return (
          <div>
            <h2 className="text-3xl font-bold text-charcoal-500 mb-2">Tell us your vision</h2>
            <p className="text-platinum-600 mb-8">
              The AI will build your itinerary around exactly these words. Skip anything you don't have strong feelings about.
            </p>

            <div className="max-w-lg mx-auto space-y-7">
              {/* Trip vibe */}
              <div>
                <label className="block text-sm font-semibold text-charcoal-500 mb-1">
                  How would you describe your dream trip?
                </label>
                <p className="text-xs text-platinum-400 mb-3">
                  e.g. "slow mornings, off the beaten path, local food, no big touristy stuff"
                </p>
                <textarea
                  value={formData.tripVibe}
                  onChange={(e) => setFormData(prev => ({ ...prev, tripVibe: e.target.value }))}
                  placeholder="Describe the vibe or feeling you're after..."
                  rows={2}
                  className="w-full px-5 py-4 text-sm bg-platinum-50 rounded-2xl shadow-inner border-0 outline-none focus:ring-2 focus:ring-coral-300 focus:bg-white resize-none placeholder:text-platinum-400 transition-all"
                />
              </div>

              {/* Must-dos */}
              <div>
                <label className="block text-sm font-semibold text-charcoal-500 mb-1">
                  Anything you absolutely must do or see?
                </label>
                <p className="text-xs text-platinum-400 mb-3">
                  e.g. "Trolltunga hike, try traditional ramen, watch a sunrise somewhere high up"
                </p>
                <textarea
                  value={formData.mustDoActivities}
                  onChange={(e) => setFormData(prev => ({ ...prev, mustDoActivities: e.target.value }))}
                  placeholder="Name the non-negotiables — we'll make sure they happen..."
                  rows={2}
                  className="w-full px-5 py-4 text-sm bg-platinum-50 rounded-2xl shadow-inner border-0 outline-none focus:ring-2 focus:ring-coral-300 focus:bg-white resize-none placeholder:text-platinum-400 transition-all"
                />
              </div>

              {/* Avoid list */}
              <div>
                <label className="block text-sm font-semibold text-charcoal-500 mb-1">
                  Anything you want to avoid?
                </label>
                <p className="text-xs text-platinum-400 mb-3">
                  e.g. "no museums, hate shopping, avoid tourist traps, don't like spicy food"
                </p>
                <textarea
                  value={formData.avoidList}
                  onChange={(e) => setFormData(prev => ({ ...prev, avoidList: e.target.value }))}
                  placeholder="Tell us what to leave out of your itinerary..."
                  rows={2}
                  className="w-full px-5 py-4 text-sm bg-platinum-50 rounded-2xl shadow-inner border-0 outline-none focus:ring-2 focus:ring-coral-300 focus:bg-white resize-none placeholder:text-platinum-400 transition-all"
                />
              </div>
            </div>
          </div>
        );

      case 9:
        if (loading) {
          return (
            <div className="flex flex-col items-center justify-center py-8">
              {/* Bouncing plane icon */}
              <motion.div
                animate={{ y: [0, -12, 0] }}
                transition={{ repeat: Infinity, duration: 2, ease: 'easeInOut' }}
                className="mb-6"
              >
                <Plane size={48} className="text-coral-400" />
              </motion.div>

              <h2 className="text-2xl font-bold text-charcoal-500 mb-2">
                Crafting your {formData.destination !== 'Undecided' ? formData.destination : ''} adventure...
              </h2>

              {/* Progress bar */}
              <div className="w-full max-w-md mx-auto mt-6 mb-4">
                <div className="h-3 bg-platinum-200 rounded-full overflow-hidden">
                  <motion.div
                    className="h-full bg-gradient-to-r from-coral-400 to-naples-400 rounded-full"
                    initial={{ width: '0%' }}
                    animate={{ width: `${loadingProgress}%` }}
                    transition={{ duration: 0.6, ease: 'easeOut' }}
                  />
                </div>
                <p className="text-xs text-platinum-500 text-right mt-1">{Math.round(loadingProgress)}%</p>
              </div>

              {/* Crossfading status message */}
              <AnimatePresence mode="wait">
                <motion.p
                  key={loadingMessage}
                  initial={{ opacity: 0, y: 8 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, y: -8 }}
                  transition={{ duration: 0.4 }}
                  className="text-platinum-600 font-medium mb-8"
                >
                  {loadingMessage}
                </motion.p>
              </AnimatePresence>

              {/* Divider */}
              <div className="w-16 h-px bg-platinum-300 mb-8" />

              {/* Crossfading travel tips */}
              <div className="max-w-md mx-auto min-h-[60px] flex items-start gap-3">
                <Lightbulb size={20} className="text-naples-500 flex-shrink-0 mt-0.5" />
                <AnimatePresence mode="wait">
                  <motion.p
                    key={currentTipIndex}
                    initial={{ opacity: 0, y: 10 }}
                    animate={{ opacity: 1, y: 0 }}
                    exit={{ opacity: 0, y: -10 }}
                    transition={{ duration: 0.5 }}
                    className="text-sm text-charcoal-400 leading-relaxed"
                  >
                    {TRAVEL_TIPS[currentTipIndex]}
                  </motion.p>
                </AnimatePresence>
              </div>
            </div>
          );
        }
        return (
          <>
          {tokenGateOpen && (
            <TokenGateModal
              actionLabel="Generate itinerary"
              cost={TOKEN_COSTS.GENERATE_ITINERARY}
              onClose={() => setTokenGateOpen(false)}
            />
          )}
          <div className="text-center">
            <h2 className="text-3xl font-bold text-charcoal-500 mb-2">
              Ready to create your trip?
            </h2>
            <p className="text-platinum-600 mb-8">
              Review your preferences and let's go
            </p>

            {/* Summary */}
            <div className="max-w-lg mx-auto bg-platinum-50 rounded-2xl shadow-lg p-6 text-left mb-8 space-y-4">
              <div className="flex items-center gap-3">
                <div className="w-9 h-9 rounded-full bg-coral-100 flex items-center justify-center flex-shrink-0">
                  <MapPin size={18} className="text-coral-500" />
                </div>
                <span className="text-charcoal-500 font-medium">
                  {formData.destination}
                  {formData.destination === 'Undecided' && formData.region && (
                    <span className="text-sm text-platinum-500 ml-2">
                      ({REGIONS.find(r => r.id === formData.region)?.name || formData.region})
                    </span>
                  )}
                </span>
              </div>
              {formData.tripOrigin && (
                <div className="flex items-center gap-3">
                  <div className="w-9 h-9 rounded-full bg-columbia-100 flex items-center justify-center flex-shrink-0">
                    <Plane size={18} className="text-columbia-500" />
                  </div>
                  <span className="text-charcoal-500">From {formData.tripOrigin}</span>
                  {formData.travelMode && (
                    <span className="text-xs px-2.5 py-1 rounded-full bg-columbia-50 text-columbia-700 font-medium capitalize">
                      {(() => { const opt = TRAVEL_MODE_OPTIONS.find(o => o.value === formData.travelMode); const MIcon = opt?.icon; return MIcon ? <MIcon size={12} className="inline mr-1" /> : null; })()}{formData.travelMode}
                    </span>
                  )}
                </div>
              )}
              <div className="flex items-center gap-3">
                <div className="w-9 h-9 rounded-full bg-columbia-100 flex items-center justify-center flex-shrink-0">
                  <Calendar size={18} className="text-columbia-500" />
                </div>
                <span className="text-charcoal-500">{formData.tripLength} days {formData.flexibleDates ? '(flexible)' : ''}</span>
              </div>
              <div className="flex items-center gap-3">
                <div className="w-9 h-9 rounded-full bg-naples-100 flex items-center justify-center flex-shrink-0">
                  <Gauge size={18} className="text-naples-600" />
                </div>
                <span className="text-charcoal-500 capitalize">{formData.travelPace} pace</span>
              </div>
              <div className="flex items-center gap-3">
                <div className="w-9 h-9 rounded-full bg-naples-100 flex items-center justify-center flex-shrink-0">
                  <DollarSign size={18} className="text-naples-600" />
                </div>
                <span className="text-charcoal-500 capitalize">{formData.budget} budget</span>
              </div>
              <div className="flex items-start gap-3">
                <div className="w-9 h-9 rounded-full bg-coral-100 flex items-center justify-center flex-shrink-0 mt-0.5">
                  <Users size={18} className="text-coral-500" />
                </div>
                <div className="flex flex-wrap gap-2">
                  {formData.travelerProfiles.map(profileId => {
                    const profile = TRAVELER_PROFILES.find(p => p.id === profileId);
                    return (
                      <span key={profileId} className="text-xs px-3 py-1.5 rounded-full bg-coral-50 text-coral-700 font-medium">
                        <IconFromName name={profile?.iconName} size={14} className="inline mr-1" style={{ color: profile?.color }} />{profile?.title}
                      </span>
                    );
                  })}
                </div>
              </div>
              {formData.whoIsComing.length > 0 && (
                <div className="flex items-start gap-3">
                  <div className="w-9 h-9 rounded-full bg-columbia-100 flex items-center justify-center flex-shrink-0 mt-0.5">
                    <Users size={18} className="text-columbia-500" />
                  </div>
                  <div className="flex flex-wrap gap-2">
                    {formData.whoIsComing.map(value => {
                      const option = WHO_IS_COMING_OPTIONS.find(o => o.value === value);
                      return (
                        <span key={value} className="text-xs px-3 py-1.5 rounded-full bg-columbia-50 text-columbia-700 font-medium">
                          <IconFromName name={option?.iconName} size={14} className="inline mr-1" />{option?.label}
                        </span>
                      );
                    })}
                  </div>
                </div>
              )}
            </div>

            {/* Intent summary from step 8 */}
            {(formData.tripVibe || formData.mustDoActivities || formData.avoidList) && (
              <div className="max-w-lg mx-auto mb-8 bg-coral-50 rounded-2xl p-4 text-left space-y-2">
                <p className="text-xs font-bold uppercase tracking-widest text-coral-400 mb-3">Your vision</p>
                {formData.tripVibe && (
                  <p className="text-sm text-charcoal-500"><span className="font-medium">Vibe:</span> {formData.tripVibe}</p>
                )}
                {formData.mustDoActivities && (
                  <p className="text-sm text-charcoal-500"><span className="font-medium">Must do:</span> {formData.mustDoActivities}</p>
                )}
                {formData.avoidList && (
                  <p className="text-sm text-charcoal-500"><span className="font-medium">Avoid:</span> {formData.avoidList}</p>
                )}
              </div>
            )}

            <motion.button
              onClick={() => handleSubmit('ai')}
              disabled={loading}
              whileHover={loading ? {} : { scale: 1.04 }}
              whileTap={loading ? {} : { scale: 0.97 }}
              className="px-10 py-4 rounded-full font-bold text-lg flex items-center gap-3 mx-auto disabled:opacity-50 bg-coral-400 hover:bg-coral-500 text-white shadow-xl hover:shadow-2xl transition-all"
            >
              <Sparkles size={22} className="animate-pulse" />
              Build My Itinerary with AI
            </motion.button>

            <div className="mt-5 flex flex-col items-center gap-1.5">
              <div className="flex items-center gap-3 text-platinum-400 text-xs">
                <span className="h-px w-8 bg-platinum-200" /> or <span className="h-px w-8 bg-platinum-200" />
              </div>
              <button
                onClick={() => handleSubmit('blank')}
                disabled={loading}
                className="mt-1 inline-flex items-center gap-2 px-6 py-2.5 rounded-full font-semibold text-sm text-charcoal-500 bg-white border border-platinum-200 hover:border-platinum-300 hover:bg-platinum-50 transition-all disabled:opacity-50"
              >
                <PencilLine size={16} />
                Start from a blank template
              </button>
              <p className="text-xs text-platinum-400 mt-1">Skip the AI — lay out the days and add everything yourself.</p>
            </div>
          </div>
          </>
        );

      default:
        return null;
    }
  };

  return (
    <div className="min-h-screen bg-naples-100">
      <div className="max-w-4xl mx-auto px-4 py-4 md:py-8">
        <ProgressBar />

        <div className="bg-white rounded-3xl shadow-xl p-5 pb-24 md:p-12 min-h-[500px] flex flex-col">
          <div className="flex-1">
            <AnimatePresence mode="wait">
              <motion.div
                key={currentStep}
                initial={{ opacity: 0, y: 12 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -12 }}
                transition={{ duration: 0.2 }}
              >
                {renderStepContent()}
              </motion.div>
            </AnimatePresence>
          </div>

          {/* Navigation — hidden during loading and at step 0 (choice screen uses card clicks) */}
          {!loading && currentStep > 0 && (
            <>
              {/* Mobile: fixed bottom bar — always reachable regardless of step content length */}
              <div className="md:hidden fixed bottom-0 left-0 right-0 bg-white border-t border-platinum-200 px-4 py-3 z-40 flex items-center justify-between shadow-lg">
                <button
                  onClick={handleBack}
                  className="flex items-center gap-2 px-5 py-2.5 rounded-full font-medium text-charcoal-400 hover:text-charcoal-600 transition-colors"
                >
                  <ArrowLeft size={16} />
                  Back
                </button>
                {currentStep < 9 && (
                  <button
                    onClick={handleNext}
                    disabled={!canProceed()}
                    className={`flex items-center gap-2 px-7 py-2.5 rounded-full font-bold transition-all disabled:opacity-40 disabled:cursor-not-allowed ${
                      canProceed()
                        ? 'bg-coral-400 text-white shadow-md hover:bg-coral-500'
                        : 'bg-platinum-200 text-platinum-500'
                    }`}
                  >
                    {tripMode === 'blank' && currentStep === 1 ? 'Create trip' : 'Continue'}
                    <ArrowRight size={16} />
                  </button>
                )}
              </div>

              {/* Desktop: inline nav inside card */}
              <div className="hidden md:flex items-center justify-between mt-8 pt-6 border-t border-platinum-100">
                <button
                  onClick={handleBack}
                  className="group flex items-center gap-2 px-6 py-3 rounded-full font-medium transition-all text-charcoal-400 hover:text-charcoal-600"
                >
                  <ArrowLeft size={18} className="transition-transform group-hover:-translate-x-1" />
                  Back
                </button>
                {currentStep < 9 && (
                  <motion.button
                    onClick={handleNext}
                    disabled={!canProceed()}
                    whileHover={canProceed() ? { scale: 1.04 } : {}}
                    whileTap={canProceed() ? { scale: 0.96 } : {}}
                    className={`flex items-center gap-2 px-8 py-3 rounded-full font-bold transition-all disabled:opacity-40 disabled:cursor-not-allowed ${
                      canProceed()
                        ? 'bg-coral-400 text-white shadow-md hover:shadow-lg hover:bg-coral-500'
                        : 'bg-platinum-200 text-platinum-500'
                    }`}
                  >
                    {tripMode === 'blank' && currentStep === 1 ? 'Create my trip' : 'Continue'}
                    <ArrowRight size={18} />
                  </motion.button>
                )}
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
}

export default CreateItineraryPage;
